import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import { fetchProfile, getCachedProfile } from '../../pro/profileCache';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { getAvatarInitials } from '../../../components/layout/appChromeMeta';
import {
  PROFILE_HUB_TABS,
  getProfileTabPath,
} from '../profileTabs';
import { getProfileHubExitPath } from '../profileHubExit';
import { CachedImage } from '../../../components/common/CachedImage';
import './profile-hub-shell.css';

function formatMemberSince(raw) {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return `MEMBER SINCE ${d.getFullYear()}`;
}

function normalizeHandle(raw, username) {
  const h = String(raw || username || '').replace(/^@+/, '').trim();
  return h ? `@${h}` : '@user';
}

function buildIdentityFromProfile(data = {}, fallbackUser = null) {
  const first = (data.first_name || fallbackUser?.first_name || '').trim();
  const last = (data.last_name || fallbackUser?.last_name || '').trim();
  const full = `${first}${last ? ` ${last}` : ''}`.trim();
  return {
    displayName: full || data.handle || fallbackUser?.username || 'Profile',
    handle: normalizeHandle(data.handle, fallbackUser?.username),
    photoUrl: data.photo_url || '',
    memberSince: formatMemberSince(data.date_joined || data.member_since || data.created_at),
  };
}

/**
 * Field-edition Profile Hub chrome: identity + tabs + close (back).
 * Body content is passed as children (My Profile / Notifications / Account / My Stuff).
 */
export function ProfileHubShell({
  activeId,
  unreadNotificationsTotal = 0,
  actions = null,
  children,
}) {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const [identity, setIdentity] = useState(() =>
    buildIdentityFromProfile(getCachedProfile({ allowStale: true }) || {}, currentUser),
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetchProfile(api);
        if (!alive) return;
        setIdentity(buildIdentityFromProfile(res?.data || {}, currentUser));
      } catch {
        if (!alive) return;
        const cached = getCachedProfile({ allowStale: true });
        if (cached) {
          setIdentity(buildIdentityFromProfile(cached, currentUser));
          return;
        }
        setIdentity(buildIdentityFromProfile({}, currentUser));
      }
    })();
    return () => {
      alive = false;
    };
  }, [currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = useCallback(() => {
    navigate(getProfileHubExitPath('/maps'));
  }, [navigate]);

  const { displayName, handle, photoUrl, memberSince } = identity;
  const initials = getAvatarInitials(displayName);
  const kickerParts = [handle];
  if (memberSince) kickerParts.push(memberSince);
  const unreadLabel = unreadNotificationsTotal > 99 ? '99+' : String(unreadNotificationsTotal);

  return (
    <div className="phub-shell" data-testid="profile-hub-shell">
      <header className="phub-shell__chrome" data-testid="profile-hub-chrome">
        <div className="phub-shell__identity-row">
          {photoUrl ? (
            <CachedImage
              src={photoUrl}
              alt=""
              className="phub-shell__avatar"
              data-testid="profile-hub-avatar"
              decoding="async"
            />
          ) : (
            <span className="phub-shell__avatar phub-shell__avatar--placeholder" aria-hidden="true">
              {initials}
            </span>
          )}
          <div className="phub-shell__identity">
            <h1 className="phub-shell__name">{displayName}</h1>
            <span className="phub-shell__kicker">{kickerParts.join(' · ')}</span>
          </div>
          {actions}
          <button
            type="button"
            className="phub-shell__close"
            aria-label="Close profile hub"
            onClick={handleClose}
          >
            ×
          </button>
        </div>
        <nav className="phub-shell__tabs" aria-label="Profile hub sections">
          {PROFILE_HUB_TABS.map((tab) => {
            const active = tab.id === activeId;
            const showBadge = tab.id === 'notifications' && unreadNotificationsTotal > 0;
            return (
              <button
                key={tab.id}
                type="button"
                className={`phub-shell__tab${active ? ' is-active' : ''}`}
                aria-current={active ? 'page' : undefined}
                onClick={() => navigate(getProfileTabPath(tab.id))}
              >
                {tab.label}
                {showBadge ? (
                  <span className="phub-shell__badge" data-testid="profile-hub-notifications-badge">
                    {unreadLabel}
                  </span>
                ) : null}
              </button>
            );
          })}
        </nav>
      </header>
      <div className="phub-shell__body">{children}</div>
    </div>
  );
}

ProfileHubShell.propTypes = {
  activeId: PropTypes.oneOf(['profile', 'notifications', 'account', 'my-stuff']).isRequired,
  unreadNotificationsTotal: PropTypes.number,
  actions: PropTypes.node,
  children: PropTypes.node,
};
